import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {DatePipe} from '@angular/common';
import {saveAs} from 'file-saver';
import {finalize, take} from 'rxjs/operators';
import {MatSort, Sort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {ActivityDisplayDTO} from '../../../models/utilisation/pha/ActivityDisplayDTO';
import {TableControl} from '../../../../common/models/TableControl';
import {DateUtil} from '../../../../common/util/DateUtil';
import {ActivityService} from '../../../services/activity.service';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {getSortDirection, getStatusColor} from '../../../../common/util/Util';
import {PagedFilterDTO} from '../../../../common/models/PagedFilterDTO';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ReportingService} from '../../../../other/services/reporting.service';
import {SharedService} from '../../../../common/services/shared-service';
import {detailsExpand} from '../../../../common';
import {PagedResponseDTO} from '../../../../common/models/PagedResponseDTO';
import {EntityTable} from '../../../../common/types/entity-table.enum';
import {EnvironmentService} from '../../../../common/services/environment-service';

enum TableColumns {
  ActivityStartDate = 'activityStartDate',
  Type = 'type',
  TariffCode = 'tariffCode',
  DiagnosisCode = 'diagnosisCode',
  Benefit = 'benefit',
  RequestedQuantity = 'requestedQuantity',
  AmountClaimed = 'amountClaimed',
  AmountBenefit = 'amountBenefit',
  AmountCoPayment = 'amountCopayment',
  AmountRejected = 'amountRejected',
  AdjudicationStatus = 'adjudicationStatus',
}

@Component({
  selector: 'app-activity-history-dialog',
  templateUrl: './activity-history-dialog.component.html',
  styleUrls: ['./activity-history-dialog.component.scss', '../../../../../../styles/global/_dialog-styles.scss'],
  animations: [detailsExpand]
})
export class ActivityHistoryDialogComponent implements OnInit {
  readonly getStatusColor = getStatusColor;

  readonly tableColumns = TableColumns;
  readonly mixtureActivityTypeId = 13;
  readonly mixtureHeaderSequence = 0;

  isBusyCounterRemittance = 0;
  transactionId: number;
  isReportable: boolean;
  isClaim: boolean;
  displayedColumns: string[] = Object.values(TableColumns);
  dataSource: MatTableDataSource<ActivityDisplayDTO> = new MatTableDataSource();
  length = 100;
  pageSize = 5;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];
  tableControl: TableControl;
  expandedRow: ActivityDisplayDTO | null;
  dateFormat = DateUtil.dateFormat;
  isBusyCounterInlineSpinner = 0; // Inline button spinner
  isProviderPortal: boolean;
  showGenerateRemittanceReportBtn: boolean;
  readonly encounterOverrideIndicatorNames: { [key: string]: string };
  readonly activityOverrideIndicatorNames: { [key: string]: string };

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    private reportingService: ReportingService,
    private activityService: ActivityService,
    public dialogRef: MatDialogRef<ActivityHistoryDialogComponent>,
    public datePipe: DatePipe,
    public sharedService: SharedService,
    private environmentService: EnvironmentService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.encounterOverrideIndicatorNames = this.environmentService.getEncounterOverrideIndicatorNames();
    this.activityOverrideIndicatorNames = this.environmentService.getActivityOverrideIndicatorNames();
    this.transactionId = data.id;
    this.isReportable = data.field != null && data.field.includes('transactionId');
    this.isProviderPortal = data.isProviderPortal;
    this.isClaim = true;
    this.tableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'activityId',
      'desc',
      data.field,
      data.id
    );
  }

  ngOnInit(): void {
    this.getListOfActivities(this.tableControl);
  }

  getListOfActivities(tableControl: TableControl): void {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblActivity;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = getSortDirection(
      tableControl.sortDirection
    );

    const pagedFilterDTO: PagedFilterDTO = new PagedFilterDTO();
    pagedFilterDTO.name = tableControl.filterField;
    pagedFilterDTO.value = tableControl.filterValue;

    const fieldMap = {};
    fieldMap[`encounterByEncounterId`] = pagedFilterDTO;
    pagedRequestDTO.fieldMap = fieldMap;

    this.activityService.getListOfActivities(pagedRequestDTO)
      .pipe(take(1))
      .subscribe({
          next: (response: PagedResponseDTO<ActivityDisplayDTO>) => {
            this.dataSource.data = this.orderMixtureFirst(response.resultList);
            if (response.resultList != null && response.resultList.length > 0) {
              this.isClaim = response.resultList[0].type == 'Claim';
            }
            this.tableControl.length = response.totalRecords;
            this.showGenerateRemittanceReportBtn = this.checkPaymentIdAndProviderIdExists();
          },
          error: (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
          }
        }
      );
  }

  pageChanged($event: PageEvent) {
    this.tableControl.pageNumber = $event.pageIndex;
    this.tableControl.pageSize = $event.pageSize;
    this.getListOfActivities(this.tableControl);
  }

  sortData($event: Sort) {
    let sortField = '';
    switch ($event.active) {
      case 'activityStartDate': {
        sortField = 'startDate';
        break;
      }
      case 'activityEndDate': {
        sortField = 'endDate';
        break;
      }
      case 'type': {
        sortField = 'encounterByEncounterId.transactionByTransactionId.transactionTypeId';
        break;
      }
      case 'requestedQuantity': {
        sortField = 'claimedQuantity';
        break;
      }
      case 'amountClaimed': {
        sortField = 'claimedQuantity';
        break;
      }
      case 'amountCopayment': {
        sortField = 'copaymentAmountClaimingCurrency';
        break;
      }
      case 'amountDeductible': {
        sortField = 'deductibleAmountClaimingCurrency';
        break;
      }
      case 'amountBenefit': {
        sortField = 'benefitAmountClaimingCurrency';
        break;
      }
      case 'amountRejected': {
        sortField = 'rejectedAmountClaimingCurrency';
        break;
      }
      case 'adjudicationStatus': {
        sortField = 'adjudicationStatusByAdjudicationStatusId.name';
        break;
      }
      case 'tariff': {
        sortField = 'tariffCode';
        break;
      }
      case 'benefit': {
        sortField = 'benefitName';
        break;
      }
      default: {
        sortField = $event.active;
        break;
      }
    }
    if (this.paginator != null) {
      this.paginator.firstPage();
    }
    this.tableControl.pageNumber = 0;
    this.tableControl.sortField = sortField;
    this.tableControl.sortDirection = $event.direction;
    this.getListOfActivities(this.tableControl);
  }

  getDateRangeDisplay(row: ActivityDisplayDTO): string {
    const start = this.datePipe.transform(row.activityStartDate, this.dateFormat);
    const end = this.datePipe.transform(row.activityEndDate, this.dateFormat);
    if (!end || start === end) {
      return start ?? '';
    }
    return `${start} - ${end}`;
  }

  isMixtureActivity(row: ActivityDisplayDTO): boolean {
    return row.activityType?.value === this.mixtureActivityTypeId;
  }

  // On the Activity Results dialog, list the mixture (header, sequence 0) first, then its ingredients
  // (sequence 1, 2...), followed by all other activities in their original order.
  private orderMixtureFirst(activities: ActivityDisplayDTO[]): ActivityDisplayDTO[] {
    if (activities == null) {
      return activities;
    }
    return [...activities].sort((a, b) => {
      const aIsMixture = this.isMixtureActivity(a);
      const bIsMixture = this.isMixtureActivity(b);
      if (aIsMixture && bIsMixture) {
        return (Number(a.mixtureSequence) || 0) - (Number(b.mixtureSequence) || 0);
      }
      return Number(bIsMixture) - Number(aIsMixture);
    });
  }

  getMixtureChipLabel(row: ActivityDisplayDTO): string {
    return (Number(row.mixtureSequence) || 0) === this.mixtureHeaderSequence ? 'Mixture' : 'Ingredient';
  }

  getTariffDisplay(row: ActivityDisplayDTO): string {
    const maxLength = 50;
    let description = row.tariffDescription ?? '';
    if (description.includes('|')) {
      description = description.split('|')[0].trim();
    }
    if (description.length > maxLength) {
      description = description.substring(0, maxLength) + '...';
    }
    return row.tariffCode ? `${row.tariffCode}: ${description}` : description;
  }

  onOkayClick() {
    this.dialogRef.close();
  }

  formatDateDisplay(date: Date) {
    return this.datePipe.transform(new Date(date), 'yyyy/MM/dd HH:mm');
  }

  onGenerateClick($event: MouseEvent): void {
    $event.preventDefault();
    this.isBusyCounterInlineSpinner++;
    this.reportingService.generateClaimOrAuthorizationReport(this.isClaim, this.transactionId)
      .pipe(
        take(1),
        finalize(() => this.isBusyCounterInlineSpinner--)
      )
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.dialogRef.close();
          }
        },
        error: (_) => {
          this.sharedService.openSnackBar('There was an error generating the report', 'Dismiss');
        }
      });
  }

  onGenerateRemittanceClick(): void {
    if (!this.checkPaymentIdAndProviderIdExists()) {
      return;
    }

    const firstRow: ActivityDisplayDTO = this.dataSource.data?.[0];
    const paymentId = firstRow?.paymentId;
    const providerId = firstRow?.provider?.providerId;
    this.isBusyCounterRemittance++;
    this.reportingService.generateReport(
      '/Reports/Revised_Remittance_Advice', 'pdf', `Payment_Id=${paymentId}&Provider_Id=${providerId}`)
      .pipe(
        take(1),
        finalize(() => this.isBusyCounterRemittance--)
      )
      .subscribe({
        next: (response) => {
          saveAs(response, `remittance_advice_provider_${paymentId}_${this.datePipe.transform(new Date(), 'yyyyMMdd_HHmm')}.pdf`);
        },
        error: (_) => {
          this.sharedService.openSnackBar('There was an error generating the report', 'Dismiss');
        }
      });
  }

  private checkPaymentIdAndProviderIdExists(): boolean {
    const firstRow: ActivityDisplayDTO = this.dataSource.data?.[0];
    const paymentId = firstRow?.paymentId;
    const providerId = firstRow?.provider?.providerId;
    return !!(paymentId && providerId);
  }
}
